// Lobby store — listagem de filas + estado de searching.
// Subscreve `lastMessage` pra reagir a queue:match_found (atualiza
// copy do modal) e game:state (fecha modal e roteia pra game view).

import { writable, derived, get } from 'svelte/store';
import { api } from '../lib/api';
import { lastMessage, send } from './ws';
import { matchSnapshot } from './match';
import type { MatchSnapshot } from './match';
import { userId, userScore, lastRankingDelta } from './auth';
import { clearPlayer } from './speech';
import { tournaments, championCelebration, isMyChampionTournament } from './tournaments';
import type { Tournament } from './tournaments';

export type Queue = {
  id: string;
  modality: 'paulista' | 'mineiro';
  format: '1v1' | '2v2';
  buy_in: number; // cents
  rake: number;   // cents
  label: string;
  enrolled?: string[];
};

export type SearchingState = {
  active: boolean;
  queue: Queue | null;
  startedAt: number;
  matchFound: boolean;
};

export const queues = writable<Queue[]>([]);
export const queuesLoading = writable<boolean>(false);
export const queuesError = writable<string>('');

export const searching = writable<SearchingState>({
  active: false,
  queue: null,
  startedAt: 0,
  matchFound: false,
});

// Buckets derivados das filas — pre-agrupados pelo template renderizar.
export const queueBuckets = derived(queues, ($queues) => {
  const buckets = {
    paulista1v1: [] as Queue[],
    mineiro1v1: [] as Queue[],
    duplas: [] as Queue[],
  };
  $queues.forEach((q) => {
    const fmt = q.format || '1v1';
    if (fmt === '2v2') {
      buckets.duplas.push(q);
      return;
    }
    if (q.modality === 'paulista') buckets.paulista1v1.push(q);
    else if (q.modality === 'mineiro') buckets.mineiro1v1.push(q);
  });
  buckets.paulista1v1.sort((a, b) => a.buy_in - b.buy_in);
  buckets.mineiro1v1.sort((a, b) => a.buy_in - b.buy_in);
  buckets.duplas.sort((a, b) => a.buy_in - b.buy_in);
  return buckets;
});

export async function loadQueues() {
  queuesError.set('');
  queuesLoading.set(true);
  try {
    const list = await api.get<Queue[]>('/queues');
    queues.set(list || []);
  } catch (e) {
    queuesError.set(String((e as Error).message ?? e));
  } finally {
    queuesLoading.set(false);
  }
}

export function enterQueue(q: Queue) {
  searching.set({
    active: true,
    queue: q,
    startedAt: Date.now(),
    matchFound: false,
  });
  send('queue:enter', { queue_id: q.id });
}

export function cancelSearching() {
  const cur = get(searching);
  if (cur.queue) {
    send('queue:leave', { queue_id: cur.queue.id });
  }
  searching.set({ active: false, queue: null, startedAt: 0, matchFound: false });
}

// Resync: chamado quando WS abre. Busca /games/current pra ver se ha
// match em andamento (cold start de Cloud Run, reconnect apos drop).
// Se houver, popula matchSnapshot — App roteia automatic pra view de game.
export async function resyncCurrentGame() {
  try {
    const snap = await api.get<MatchSnapshot>('/games/current');
    if (snap && snap.id) {
      matchSnapshot.set(snap);
    }
  } catch (e) {
    // 404 = sem match ativo (caso normal). Outros erros nao bloqueiam.
    const err = e as { status?: number };
    if (err.status && err.status !== 404) {
      console.warn('resync /games/current falhou:', e);
    }
  }
}

// Subscreve eventos WS pra reagir a match_found / game:state.
// Este side-effect roda no module load — fica vivo durante a sessao.
lastMessage.subscribe((msg) => {
  if (!msg) return;
  switch (msg.event) {
    case 'queue:match_found':
      searching.update((s) => ({ ...s, matchFound: true }));
      break;
    case 'game:state': {
      // Match comecou — fecha modal e popula matchSnapshot pro App
      // rotear pra view de game.
      const snap = msg.data as unknown as MatchSnapshot;
      matchSnapshot.set(snap);
      // Limpa delta de ranking da partida anterior quando uma nova
      // comeca — evita mostrar "+10" velho numa partida sem ranking.
      if (snap.status !== 'finished') lastRankingDelta.set(null);
      const cur = get(searching);
      if (cur.active) {
        searching.set({ active: false, queue: null, startedAt: 0, matchFound: false });
      }
      break;
    }
    case 'game:finished': {
      // Match terminou — mantemos matchSnapshot pra finished modal mostrar,
      // mas marca status pro App saber.
      matchSnapshot.update((m) => (m ? { ...m, status: 'finished' } : m));
      break;
    }
    case 'ranking:update': {
      // Delta de ranking de partida de torneio (+10/-10). Atualiza score
      // do topbar + guarda o delta pro FinishedModal mostrar.
      const d = msg.data as { delta?: number; new_score?: number };
      if (typeof d.delta === 'number') {
        if (typeof d.new_score === 'number') userScore.set(d.new_score);
        lastRankingDelta.update((prev) => ({
          delta: d.delta as number,
          newScore: d.new_score ?? 0,
          seq: (prev?.seq ?? 0) + 1,
        }));
      }
      break;
    }
    case 'tournament:state': {
      // Torneio em que o user esta inscrito mudou (iniciou / avancou
      // round / terminou). Atualiza tournaments store pra UI re-renderizar
      // automaticamente (sem precisar de refresh manual).
      const t = msg.data as unknown as Tournament;
      if (t && t.id) {
        tournaments.update((list) => {
          const exists = list.find((x) => x.id === t.id);
          if (exists) return list.map((x) => (x.id === t.id ? t : x));
          return [t, ...list];
        });
        // Venci o torneio inteiro? Dispara a tela de campeao.
        if (t.status === 'finished' && isMyChampionTournament(t, get(userId))) {
          championCelebration.set(t);
        }
      }
      break;
    }
    case 'error': {
      // Backend rejeitou um comando do user (race condition: parceiro do
      // mesmo time agiu primeiro em truco/mao 11). Limpa o bubble local
      // "otimista" desse user pra UX nao ficar inconsistente. Felipe
      // 2026-05-22: "quando server rejeita, limpar bubble".
      const data = msg.data as { for?: string; message?: string };
      const uid = get(userId);
      if (uid && data?.for && /^game:(truco|mao):/.test(data.for)) {
        clearPlayer(uid);
      }
      break;
    }
  }
});
